/**
 * Pure split-tree model for terminal panes (tmux / Windows Terminal style).
 *
 *   { t: 'leaf',  pane: 'p_…' }
 *   { t: 'split', id: 's_…', dir: 'row' | 'col', ratio: 0.5, a: Node, b: Node }
 *
 * `dir: 'row'` = side by side (vertical divider), `'col'` = stacked
 * (horizontal divider). `ratio` is the share of `a`. Nothing here touches
 * React, the store or the DOM — the store calls these, the grid renders
 * `computeRects`, and the layouts code serialises through them.
 */

export const MAX_PANES = 8;
export const MIN_PANE_W = 240;
export const MIN_PANE_H = 96;
export const GUTTER = 4;
const RATIO_MIN = 0.1;
const RATIO_MAX = 0.9;

let splitSeq = 0;
export const newSplitId = () => `s_${Date.now().toString(36)}_${(splitSeq += 1)}`;

export const leaf = (pane) => ({ t: 'leaf', pane });
export const split = (dir, a, b, ratio = 0.5, id = newSplitId()) => ({
  t: 'split',
  id,
  dir,
  ratio: clampRatio(ratio),
  a,
  b,
});

export function clampRatio(r) {
  const n = Number(r);
  if (!Number.isFinite(n)) return 0.5;
  return Math.min(RATIO_MAX, Math.max(RATIO_MIN, n));
}

export function countLeaves(node) {
  if (!node) return 0;
  return node.t === 'leaf' ? 1 : countLeaves(node.a) + countLeaves(node.b);
}

/** Pane ids in reading order (a before b, depth first). */
export function leafOrder(node, out = []) {
  if (!node) return out;
  if (node.t === 'leaf') out.push(node.pane);
  else {
    leafOrder(node.a, out);
    leafOrder(node.b, out);
  }
  return out;
}

export function hasLeaf(node, paneId) {
  return leafOrder(node).includes(paneId);
}

/** First leaf of a subtree — where focus lands when its sibling closes. */
export function firstLeaf(node) {
  if (!node) return null;
  return node.t === 'leaf' ? node.pane : firstLeaf(node.a);
}

/**
 * Split the leaf `paneId` in `dir`; the new pane goes right ('row') or below
 * ('col'). Returns the same tree object when the leaf is not found.
 */
export function splitLeaf(node, paneId, dir, newPaneId, ratio = 0.5) {
  if (!node) return node;
  if (node.t === 'leaf') {
    return node.pane === paneId ? split(dir, node, leaf(newPaneId), ratio) : node;
  }
  const a = splitLeaf(node.a, paneId, dir, newPaneId, ratio);
  if (a !== node.a) return { ...node, a };
  const b = splitLeaf(node.b, paneId, dir, newPaneId, ratio);
  if (b !== node.b) return { ...node, b };
  return node;
}

/**
 * Remove a leaf; its parent split is replaced by the sibling subtree.
 * Returns `{ tree, focus }` — `tree` is null when the last leaf went, and
 * `focus` is the sibling's first leaf (where focus should move).
 */
export function removeLeaf(node, paneId) {
  if (!node) return { tree: null, focus: null };
  if (node.t === 'leaf') {
    return node.pane === paneId ? { tree: null, focus: null } : { tree: node, focus: null };
  }
  if (node.a.t === 'leaf' && node.a.pane === paneId) return { tree: node.b, focus: firstLeaf(node.b) };
  if (node.b.t === 'leaf' && node.b.pane === paneId) return { tree: node.a, focus: firstLeaf(node.a) };
  const ra = removeLeaf(node.a, paneId);
  if (ra.tree !== node.a) return { tree: { ...node, a: ra.tree }, focus: ra.focus };
  const rb = removeLeaf(node.b, paneId);
  if (rb.tree !== node.b) return { tree: { ...node, b: rb.tree }, focus: rb.focus };
  return { tree: node, focus: null };
}

function mapSplit(node, splitId, fn) {
  if (!node || node.t === 'leaf') return node;
  if (node.id === splitId) return fn(node);
  const a = mapSplit(node.a, splitId, fn);
  const b = mapSplit(node.b, splitId, fn);
  return a === node.a && b === node.b ? node : { ...node, a, b };
}

export const setRatio = (node, splitId, r) =>
  mapSplit(node, splitId, (s) => ({ ...s, ratio: clampRatio(r) }));
export const resetRatio = (node, splitId) => setRatio(node, splitId, 0.5);

/** The nearest ancestor split of `paneId` with direction `dir`. */
export function nearestSplit(node, paneId, dir, found = null) {
  if (!node || node.t === 'leaf') return node?.pane === paneId ? found : null;
  const here = node.dir === dir ? node : found;
  return nearestSplit(node.a, paneId, dir, here) || nearestSplit(node.b, paneId, dir, here);
}

/** Minimum pixel size a subtree needs along an axis ('w' or 'h'). */
function minSize(node, axis, gutter) {
  if (node.t === 'leaf') return axis === 'w' ? MIN_PANE_W : MIN_PANE_H;
  const along = (node.dir === 'row' && axis === 'w') || (node.dir === 'col' && axis === 'h');
  const a = minSize(node.a, axis, gutter);
  const b = minSize(node.b, axis, gutter);
  return along ? a + gutter + b : Math.max(a, b);
}

/**
 * Pixel rects for every pane and every divider of a tree laid out in a
 * `w × h` box. Ratios are clamped to the panes' pixel minimums here, at
 * render time — the stored ratio is never rewritten by a window shrink.
 */
export function computeRects(node, w, h, gutter = GUTTER) {
  const panes = {};
  const dividers = [];
  const walk = (n, x, y, width, height) => {
    if (!n) return;
    if (n.t === 'leaf') {
      panes[n.pane] = { x, y, w: Math.max(0, width), h: Math.max(0, height) };
      return;
    }
    const row = n.dir === 'row';
    const total = (row ? width : height) - gutter;
    const axis = row ? 'w' : 'h';
    const minA = minSize(n.a, axis, gutter);
    const minB = minSize(n.b, axis, gutter);
    let sizeA = Math.round(total * n.ratio);
    if (total >= minA + minB) sizeA = Math.min(total - minB, Math.max(minA, sizeA));
    const sizeB = total - sizeA;
    if (row) {
      walk(n.a, x, y, sizeA, height);
      dividers.push({ id: n.id, dir: n.dir, ratio: n.ratio, x: x + sizeA, y, w: gutter, h: height, span: { x, y, w: width, h: height } });
      walk(n.b, x + sizeA + gutter, y, sizeB, height);
    } else {
      walk(n.a, x, y, width, sizeA);
      dividers.push({ id: n.id, dir: n.dir, ratio: n.ratio, x, y: y + sizeA, w: width, h: gutter, span: { x, y, w: width, h: height } });
      walk(n.b, x, y + sizeA + gutter, width, sizeB);
    }
  };
  walk(node, 0, 0, w, h);
  return { panes, dividers };
}

/**
 * Can `paneId` be split in `dir` inside a `w × h` grid?
 * Returns `{ ok, reason }` with reason 'limit' (8 panes) or 'small'.
 * With no size known yet (w/h falsy) only the count limit applies.
 */
export function canSplit(node, paneId, dir, w, h, gutter = GUTTER) {
  if (countLeaves(node) >= MAX_PANES) return { ok: false, reason: 'limit' };
  if (!w || !h) return { ok: true, reason: null };
  const r = computeRects(node, w, h, gutter).panes[paneId];
  if (!r) return { ok: false, reason: 'missing' };
  const fits = dir === 'row' ? r.w >= MIN_PANE_W * 2 + gutter : r.h >= MIN_PANE_H * 2 + gutter;
  return fits ? { ok: true, reason: null } : { ok: false, reason: 'small' };
}

/**
 * Geometric neighbour of `paneId` in direction 'left'|'right'|'up'|'down':
 * the pane across the nearest edge whose span overlaps the focused pane's
 * edge the most; ties go to the most recently focused (`mru`, newest first).
 */
export function neighbour(rects, paneId, direction, mru = []) {
  const me = rects[paneId];
  if (!me) return null;
  const horizontal = direction === 'left' || direction === 'right';
  let best = null;
  Object.entries(rects).forEach(([id, r]) => {
    if (id === paneId) return;
    let gap;
    if (direction === 'right') gap = r.x - (me.x + me.w);
    else if (direction === 'left') gap = me.x - (r.x + r.w);
    else if (direction === 'down') gap = r.y - (me.y + me.h);
    else gap = me.y - (r.y + r.h);
    if (gap < 0) return;
    const overlap = horizontal
      ? Math.min(me.y + me.h, r.y + r.h) - Math.max(me.y, r.y)
      : Math.min(me.x + me.w, r.x + r.w) - Math.max(me.x, r.x);
    if (overlap <= 0) return;
    const rank = mru.indexOf(id);
    const cand = { id, gap, overlap, rank: rank === -1 ? Infinity : rank };
    if (
      !best ||
      cand.gap < best.gap ||
      (cand.gap === best.gap && cand.overlap > best.overlap) ||
      (cand.gap === best.gap && cand.overlap === best.overlap && cand.rank < best.rank)
    ) {
      best = cand;
    }
  });
  return best ? best.id : null;
}

/** Next/previous pane in reading order, wrapping. */
export function cycle(node, paneId, delta) {
  const order = leafOrder(node);
  if (order.length === 0) return null;
  const i = order.indexOf(paneId);
  return order[(((i === -1 ? 0 : i) + delta) % order.length + order.length) % order.length];
}

/**
 * Serialise a tree for a saved layout: each leaf's pane id is replaced by
 * its slot index (reading order).
 */
export function serializeTree(node) {
  const order = leafOrder(node);
  const walk = (n) =>
    n.t === 'leaf'
      ? { t: 'leaf', slot: order.indexOf(n.pane) }
      : { t: 'split', dir: n.dir, ratio: clampRatio(n.ratio), a: walk(n.a), b: walk(n.b) };
  return node ? walk(node) : null;
}

/**
 * Validate and rebuild a serialised tree. Unknown node types or directions
 * reject the whole tree (null). Ratios are clamped; slots are renumbered in
 * reading order and anything past MAX_PANES is dropped (its parent split
 * collapses to the surviving sibling). `paneFor(slotIndex)` supplies ids.
 */
export function deserializeTree(obj, paneFor) {
  let slot = 0;
  let bad = false;
  const walk = (n, depth) => {
    if (bad || !n || typeof n !== 'object' || depth > 16) {
      bad = true;
      return null;
    }
    if (n.t === 'leaf') {
      if (slot >= MAX_PANES) return null;
      const id = paneFor(slot);
      slot += 1;
      return leaf(id);
    }
    if (n.t === 'split' && (n.dir === 'row' || n.dir === 'col')) {
      const a = walk(n.a, depth + 1);
      const b = walk(n.b, depth + 1);
      if (bad) return null;
      if (!a) return b;
      if (!b) return a;
      return split(n.dir, a, b, n.ratio);
    }
    bad = true;
    return null;
  };
  const tree = walk(obj, 0);
  return bad ? null : tree;
}
